import * as _ from 'underscore';

import { Formatters, Messages } from '../../../codegen/Localize';
import { IError } from '../../../httpClient/serverApi/types/IError';
import { CommonErrorCode, LdapErrorCode, UserErrorCode } from '../../../ts/helpers/ErrorCodes';
import { L10n } from '../../../ts/L10n/L10n';
import { Set } from '../../../ts/models/Set';
import { IUser } from '../../../ts/ServerApi/types/IUser';
import { IResultCodes, IUserActionError } from '../../../ts/ServerApi/types/IUserActionResult';
import { ToasterService, ToastType } from '../../../ts/services/ToasterService';
import { NotificationHelper } from '../../components/helpers/NotificationHelper';
import { LicenseTierFilter } from '../filters/LicenseTierFilter';
import { SiteRoleOptionsResource } from '../resources/SiteRoleOptions';
import { ResultCode } from '../UserActionResultCode';

// Notification For:
// => generic user actions.

export interface UserActionErrorNotificationParams {
  users: Set<IUser>;          // the users the action was taken on.
  errors: IUserActionError[];           // the errors objects from the API response.

  // TODO: this can be removed
  showErrorMsgFunc?: (errorMsg: string) => void;  // a fn to call on error (instead of toasting); optional.
}

export interface UserActionNotificationParams extends UserActionErrorNotificationParams {
  results: IResultCodes[];              // the result objects from the API response.
  successCode: string;                            // the result code which indicates total success.
  siteRole?: string;                  //Site role that the user is set to when they are added.
}

export class UserActionNotification {

  public static readonly instance: UserActionNotification = new UserActionNotification();

  public notify(params: UserActionNotificationParams) {
    _.defaults(params, {
      results: [],
      errors: [],
    });

    this.notifyErrors(params);

    // handle results (successes & partial successes)
    const errorsByUserId = _.groupBy(params.errors, 'id');
    const usersMappedByResults = this.getUsersByResultMap(params.users, params.results, params.successCode, errorsByUserId);
    _.each(usersMappedByResults, (usersForResult: IUser[], resultCode: string) => {
      const toastType = (resultCode === params.successCode)
        ? ToastType.Success
        : ToastType.Error;

      // TODO: refactor getUsersByResultMap() to make this not necessary.
      const users = _.unique(usersForResult);
      let msg;
      //get the license tier
      let translatedLicenseRole: string = undefined;
      let siteRolesOptionsResource: SiteRoleOptionsResource = SiteRoleOptionsResource.instance;
      if (params.siteRole) {
        let siteRoleOption = siteRolesOptionsResource.getSiteRoleOptionBySiteRole(params.siteRole);
        translatedLicenseRole = LicenseTierFilter.filter(siteRoleOption.licenseTierRole);
      }
      if(resultCode === ResultCode.InsufficientLicense.toString()){ //Insufficient licenses
        msg = this.getErrorMessageForInsufficientLicenses(translatedLicenseRole, users);
      } else {
        msg = (users.length > 1)
          ? this.multiResultMessageFor(resultCode, users.length)
          : this.resultMessageFor(resultCode, NotificationHelper.nameFor(users[0]), translatedLicenseRole);
      }
      ToasterService.instance.pop({ type: toastType, text: msg });
    });

  }

  private getErrorMessageForInsufficientLicenses(translatedLicenseRole: string, users: IUser[]): string{
    //single user vs multi-user
    if(users.length > 1){
      return Messages.userActionBulkOperationResult_31({COUNT: users.length, COUNT_LOC: Formatters.formatCount(users.length)});
    } else {
      return Messages.userActionResult_31({ USER_NAME: L10n.elideIfLong(NotificationHelper.nameFor(users[0])) , LICENSE_TIER: translatedLicenseRole});
    }
  }

  public notifyErrors(params: UserActionErrorNotificationParams) {
    _.defaults(params, {
      errors: [],
    });

    // handle errors (failures)
    if (params.errors.length > 0) {
      _.each(params.errors, (error: IUserActionError) => {
        const user = params.users.get(error.id);
        const nameForMsg = (user && NotificationHelper.nameFor(user)) || error.username;

        if (_.isFunction(params.showErrorMsgFunc)) {
          params.showErrorMsgFunc(this.errorMessageFor(error, nameForMsg));
        } else {
          ToasterService.instance.pop({ type: ToastType.Error, text: this.errorMessageFor(error, nameForMsg)});
        }
      });
    }
  }

  /*
   * TODO: we need to know the action that was being attempted in order to produce
   *       more specific error messages.
   *       The design of this class should probably be reworked to handle that.
   */
  private errorMessageFor(error: IError, username: string): string {
    const errorCode = error.code;

    if (LdapErrorCode[errorCode]) {
      return this.ldapErrorMessage(error, username);
    }

    switch (errorCode) {
      case UserErrorCode.UserAlreadyExists:
        return Messages.errorUserAlreadyExists({ NEW_ITEM_NAME: L10n.elideIfLong(username) });
      case UserErrorCode.UserNotSupportedGuestOperation:
        return Messages.errorUserNotSupportedGuestOperation();
      case UserErrorCode.UserNotSupportedSelfOperation:
        return Messages.errorUserNotSupportedSelfOperation();
      case UserErrorCode.UserInsufficientSiteQuota:
        return Messages.errorUserInsufficientSiteQuota();
      case UserErrorCode.UserCannotBeDeletedIfAllUsersGroupGrantSiteRoleOnLogin:
        return Messages.errorUserCannotBeDeletedIfAllUsersGroupGrantSiteRoleOnLogin({ USER_NAME: L10n.elideIfLong(username) });
      case UserErrorCode.UserCannotHaveSiteRoleLowerThanHighestMinimumLulGroupRole:
        return Messages.errorUserCannotHaveSiteRoleLowerThan_AllUsersMinimumLulGroupRole();
      case CommonErrorCode.CallingUserInsufficientPermissions:
        return Messages.errorInsufficientPermision({ USER_NAME: L10n.elideIfLong(username) });
      case CommonErrorCode.ADUserNotFound:
        return Messages.errorUserNotFoundInAD({ USER_NAME: L10n.elideIfLong(username) });
      case CommonErrorCode.UnexpectedErrorOccurred:
        return Messages.unexpectedErrorOnServer();
      case CommonErrorCode.SystemUserCannotBeSiteSAMLUser:
        return Messages.systemAdminNotAllowedAsSiteSAMLUser();
      case UserErrorCode.UserPasswordNotRespectingPolicies:
        return Messages.errorUserPasswordNotRespectingPolicies();
      default:
        return Messages.errorUnknown();
    }
  }

  private ldapErrorMessage(error: IError, username: string): string {
    const errorCode = error.code;

    switch (errorCode) {
      case LdapErrorCode.LdapServerDown:
        return Messages.errorLdapServerDown({ USER_NAME: L10n.elideIfLong(username) });
      case LdapErrorCode.LdapInvalidCredentials:
        return Messages.errorLdapInvalidCredentials({ USER_NAME: L10n.elideIfLong(username) });
      default:
        return Messages.errorUnknownLdapError({ USER_NAME: L10n.elideIfLong(username) });
    }
  }

  private resultMessageFor(resultCode: string, username: string, translatedLicenseRole?: string): string {
    return NotificationHelper.userActionResultLocKeyDict[parseInt(resultCode, 10)](username, translatedLicenseRole);
  }

  private multiResultMessageFor(resultCode: string, count: number): string {
    return NotificationHelper.userActionBulkOperationResultLocKeyDict[parseInt(resultCode, 10)](count);
  }

  // returns a dictionary of users keyed by result code.
  // each user might appear multiple times (multiple result codes per user).
  // if a user is not associated with any result codes, this function associates them with the success code.
  private getUsersByResultMap(users: Set<IUser>, results: IResultCodes[], successCode: string, errorsByUserId: _.Dictionary<IUserActionError[]>): _.Dictionary<IUser[]> {
    const resultSet = Set.fromIdItemArray(results);

    const map: _.Dictionary<IUser[]> = {};
    users.each((user: IUser) => {
      const resultsForThisUser: IResultCodes = resultSet.get(user.id);
      const resultCodesForThisUser: number[] = resultsForThisUser && resultSet.get(user.id).codes || [];

      if (resultCodesForThisUser.length > 0) {
        // there were partial success results for this user.  map them.
        _.each(resultCodesForThisUser, (resultCode: number) => {
          // Check for op codes in the results array that are considered errors.
          // Map non-errors to the sucess result code.
          const resultToNotifyFor = this.isErrorResult(resultCode) ? resultCode.toString() : successCode;

          if (map[resultToNotifyFor]) {
            map[resultToNotifyFor].push(user);
          } else {
            map[resultToNotifyFor] = [user];
          }
        });
      } else if (!errorsByUserId[user.id]) {
        // If there are no result codes or error codes for this user, treat operation as a success.
        if (map[successCode]) {
          map[successCode].push(user);
        } else {
          map[successCode] = [user];
        }
      }
    });
    return map;
  }

  private isErrorResult(resultCode: number): boolean {
    switch (resultCode) {
      case 14: // user that owns content on site/server, gets unlicensed instead
      case 15: // server admin that owns content on site/server cannot be deleted
      case 32: // site at quota
      case 31: // not enough licenses to add to site
      case 42: // not enough licenses to promote to admin
      case 52: // can't operate on self
      case 63: // Cannot delete user who owns content when All users group is set to LUL
        return true;
      default:
        return false;
    }
  }
}

export default UserActionNotification.instance;
